import type { Labelled, StatusKind } from './common.js';

/**
 * غرفة العمليات.
 *
 * الفكرة أن النظام لا ينتظر أن يسأله أحد. البيانات موجودة أصلًا — عميل لم
 * يُتَّصل به منذ أسبوع، عرض ينتهي غدًا، فاتورة تأخرت شهرًا — لكنها موزّعة
 * على شاشات لا يفتحها أحد في الوقت المناسب. غرفة العمليات تقرأها كلها
 * وتضع ما يستحق التدخل اليوم في مكان واحد.
 *
 * الإشارات تُحسب لحظيًا لا تُخزَّن كمهام.
 *
 * لو خُزِّنت لظلت مهمة "اتصل بهذا العميل" قائمة بعد أن اتُّصل به فعلًا، حتى
 * يمر أحد ويغلقها يدويًا. الحساب اللحظي يجعل الإشارة تختفي بمجرد زوال
 * سببها، فما يبقى على الشاشة يستحق التدخل بالفعل. ما يُخزَّن هو التصرّف
 * البشري وحده: صرف الإشارة أو تأجيلها.
 */

/* ==========================================================================
   الأولويات
   ========================================================================== */

export const WAR_ROOM_PRIORITIES = ['CRITICAL', 'HIGH', 'NORMAL'] as const;
export type WarRoomPriority = (typeof WAR_ROOM_PRIORITIES)[number];

export const WAR_ROOM_PRIORITY_DEFS: readonly Labelled<WarRoomPriority>[] = [
  { key: 'CRITICAL', label: 'حرج', kind: 'danger' },
  { key: 'HIGH', label: 'مرتفع', kind: 'warning' },
  { key: 'NORMAL', label: 'عادي', kind: 'info' },
] as const;

export const WAR_ROOM_PRIORITY_MAP = Object.fromEntries(
  WAR_ROOM_PRIORITY_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<WarRoomPriority, Labelled<WarRoomPriority>>>;

export const PRIORITY_ORDER: Readonly<Record<WarRoomPriority, number>> = {
  CRITICAL: 0,
  HIGH: 1,
  NORMAL: 2,
};

/* ==========================================================================
   نوع السجل الذي تشير إليه الإشارة
   ========================================================================== */

export const SIGNAL_SUBJECTS = [
  'LEAD',
  'DEAL',
  'QUOTATION',
  'INVOICE',
  'EVENT',
  'CONTRACT',
  'ACTIVITY',
  'ACCOUNT',
] as const;
export type SignalSubject = (typeof SIGNAL_SUBJECTS)[number];

/** مسار الشاشة التي تفتح السجل. */
export const SUBJECT_ROUTES: Readonly<Record<SignalSubject, string>> = {
  LEAD: '/crm/leads',
  DEAL: '/sales/deals',
  QUOTATION: '/sales/quotations',
  INVOICE: '/finance/invoices',
  EVENT: '/crm/calendar',
  CONTRACT: '/sales/contracts',
  ACTIVITY: '/crm/activities',
  ACCOUNT: '/crm/accounts',
};

/** اسمُ الوحدةِ التي تخصُّها الإشارة — لتجميعِ القواعدِ في شاشة الضبط. */
export const SUBJECT_LABELS: Readonly<Record<SignalSubject, string>> = {
  LEAD: 'العملاء المحتملون',
  DEAL: 'الصفقات',
  QUOTATION: 'عروض الأسعار',
  INVOICE: 'الفواتير',
  EVENT: 'المواعيد',
  CONTRACT: 'العقود',
  ACTIVITY: 'المهامّ والأنشطة',
  ACCOUNT: 'العملاء',
};

/* ==========================================================================
   القواعد
   ========================================================================== */

export const WAR_ROOM_RULE_KEYS = [
  'STALE_LEAD',
  'UNTOUCHED_LEAD',
  'UNASSIGNED_LEAD',
  'STALLED_DEAL',
  'EXPIRING_QUOTATION',
  'UNDECIDED_QUOTATION',
  'OVERDUE_INVOICE',
  'BIG_DEAL_AT_RISK',
  'UNCONFIRMED_EVENT',
  'EXPIRING_CONTRACT',
  'OVERDUE_TASK',
  'DORMANT_ACCOUNT',
] as const;
export type WarRoomRuleKey = (typeof WAR_ROOM_RULE_KEYS)[number];

export interface WarRoomRuleDef {
  key: WarRoomRuleKey;
  label: string;
  /** ما الذي ترصده القاعدة ولماذا يستحق التدخل. */
  description: string;
  subject: SignalSubject;
  defaultPriority: WarRoomPriority;
  /** هل تعمل القاعدة ما لم يُوقفها المسؤول. */
  defaultEnabled: boolean;
  /** حد التفعيل: عدد الأيام أو قيمة مالية حسب الوحدة. */
  defaultThreshold: number;
  thresholdUnit: 'DAYS' | 'MONEY';
  /** نص الحد في شاشة الإعدادات. */
  thresholdLabel: string;
  /** الإجراء المقترح، وهو ما تُنشئه حملة الاتصال. */
  action: 'CALL' | 'TASK' | 'MAIL';
  /** صيغة موضوع النشاط عند إنشاء حملة. */
  campaignSubject: string;
}

export const WAR_ROOM_RULES: readonly WarRoomRuleDef[] = [
  {
    key: 'UNTOUCHED_LEAD',
    label: 'عميل محتمل لم يُتواصل معه',
    description:
      'عميل جديد مضى على تسجيله أكثر من الحد دون أي تواصل. أول ساعات العميل الجديد هي أعلى فرصه في التحول، وتأخّر الرد يهديها لمن يرد أسرع.',
    subject: 'LEAD',
    defaultPriority: 'CRITICAL',
    defaultEnabled: true,
    defaultThreshold: 1,
    thresholdUnit: 'DAYS',
    thresholdLabel: 'يمضي على التسجيل أكثر من',
    action: 'CALL',
    campaignSubject: 'اتصال أول بعميل محتمل جديد',
  },
  {
    key: 'STALE_LEAD',
    label: 'عميل محتمل بلا متابعة',
    description:
      'عميل قيد العمل انقطع التواصل معه أكثر من الحد. الانقطاع هو السبب الأشيع لضياع عميل كان مهتمًا.',
    subject: 'LEAD',
    defaultPriority: 'HIGH',
    defaultEnabled: true,
    defaultThreshold: 5,
    thresholdUnit: 'DAYS',
    thresholdLabel: 'ينقطع التواصل أكثر من',
    action: 'CALL',
    campaignSubject: 'مكالمة متابعة',
  },
  {
    key: 'STALLED_DEAL',
    label: 'صفقة راكدة',
    description:
      'صفقة مفتوحة لم يتغير فيها شيء منذ أكثر من الحد. الصفقة التي لا تتحرك لا تبقى محايدة، بل تبرد حتى تُخسر دون قرار.',
    subject: 'DEAL',
    defaultPriority: 'HIGH',
    defaultEnabled: true,
    defaultThreshold: 14,
    thresholdUnit: 'DAYS',
    thresholdLabel: 'تبقى بلا تحديث أكثر من',
    action: 'CALL',
    campaignSubject: 'متابعة صفقة متوقفة',
  },
  {
    key: 'EXPIRING_QUOTATION',
    label: 'عرض سعر يوشك أن ينتهي',
    description:
      'عرض مُرسَل تنتهي صلاحيته خلال الحد ولم يُبَتّ فيه. الاتصال قبل انتهاء الصلاحية يعطي سببًا طبيعيًا للمتابعة ويصنع إلحاحًا.',
    subject: 'QUOTATION',
    defaultPriority: 'CRITICAL',
    defaultEnabled: true,
    defaultThreshold: 3,
    thresholdUnit: 'DAYS',
    thresholdLabel: 'تنتهي صلاحيته خلال',
    action: 'CALL',
    campaignSubject: 'متابعة عرض سعر قبل انتهاء صلاحيته',
  },
  {
    key: 'UNDECIDED_QUOTATION',
    label: 'عرض بلا قرار',
    description:
      'عرض مضى على إرساله أكثر من الحد دون قبول أو رفض. الصمت ليس رفضًا، لكنه يصير رفضًا إن تُرك.',
    subject: 'QUOTATION',
    defaultPriority: 'HIGH',
    defaultEnabled: true,
    defaultThreshold: 7,
    thresholdUnit: 'DAYS',
    thresholdLabel: 'يمضي على إرساله أكثر من',
    action: 'CALL',
    campaignSubject: 'استيضاح قرار العميل في عرض السعر',
  },
  {
    key: 'OVERDUE_INVOICE',
    label: 'فاتورة متأخرة',
    description:
      'فاتورة تجاوزت موعد استحقاقها بأكثر من الحد. كل يوم تأخير إضافي يقلّل احتمال التحصيل.',
    subject: 'INVOICE',
    defaultPriority: 'CRITICAL',
    defaultEnabled: true,
    defaultThreshold: 3,
    thresholdUnit: 'DAYS',
    thresholdLabel: 'تتأخر عن الاستحقاق أكثر من',
    action: 'CALL',
    campaignSubject: 'مكالمة تحصيل',
  },
  {
    key: 'BIG_DEAL_AT_RISK',
    label: 'صفقة كبيرة معرّضة',
    description:
      'صفقة قيمتها تتجاوز الحد وتاريخ إغلاقها المتوقع مضى أو يقترب دون تقدم. خسارة صفقة واحدة كبيرة تعادل عشر صغيرة.',
    subject: 'DEAL',
    defaultPriority: 'CRITICAL',
    defaultEnabled: true,
    defaultThreshold: 50_000,
    thresholdUnit: 'MONEY',
    thresholdLabel: 'قيمتها لا تقل عن',
    action: 'TASK',
    campaignSubject: 'مراجعة خطة إغلاق صفقة كبيرة',
  },
  {
    key: 'UNCONFIRMED_EVENT',
    label: 'موعد قريب بلا تأكيد',
    description:
      'موعد خلال الحد ما زال مبدئيًا. الموعد غير المؤكد يحجز موردًا ووقت فريق قد يضيعان.',
    subject: 'EVENT',
    defaultPriority: 'HIGH',
    defaultEnabled: true,
    defaultThreshold: 2,
    thresholdUnit: 'DAYS',
    thresholdLabel: 'يبدأ خلال',
    action: 'CALL',
    campaignSubject: 'تأكيد موعد',
  },
  {
    key: 'UNASSIGNED_LEAD',
    label: 'عميل محتمل بلا مسؤول',
    description:
      'عميل مفتوح مضى على تسجيله أكثر من الحد ولا مالك له. ما لا يملكه أحدٌ لا يعمل عليه أحد — فيبقى في القائمة حتى يبرد.',
    subject: 'LEAD',
    defaultPriority: 'CRITICAL',
    defaultEnabled: true,
    defaultThreshold: 1,
    thresholdUnit: 'DAYS',
    thresholdLabel: 'يبقى بلا مالك أكثر من',
    action: 'TASK',
    campaignSubject: 'إسناد عميل محتمل إلى مسؤول',
  },
  {
    key: 'EXPIRING_CONTRACT',
    label: 'عقد يقترب انتهاؤه',
    description:
      'عقد سارٍ ينتهي خلال الحد. التجديد يُتفاوض عليه قبل الانتهاء لا بعده — وبعد انقضائه تصير المحادثة بيعًا من جديد لا تجديدًا.',
    subject: 'CONTRACT',
    defaultPriority: 'HIGH',
    defaultEnabled: true,
    defaultThreshold: 30,
    thresholdUnit: 'DAYS',
    thresholdLabel: 'ينتهي خلال',
    action: 'CALL',
    campaignSubject: 'التفاوض على تجديد عقد',
  },
  {
    key: 'OVERDUE_TASK',
    label: 'مهمّة تجاوزت موعدها',
    description:
      'مهمّة أو نشاط مخطَّط مضى موعده بأكثر من الحد ولم يُنجز. وهي تُغلق الدائرة: ما تُنشئه حملاتُ الغرفة يعود إليها إن لم يُنفَّذ.',
    subject: 'ACTIVITY',
    defaultPriority: 'HIGH',
    defaultEnabled: true,
    defaultThreshold: 1,
    thresholdUnit: 'DAYS',
    thresholdLabel: 'يتأخّر عن موعده أكثر من',
    action: 'TASK',
    campaignSubject: 'متابعة مهمّة متأخّرة',
  },
  {
    key: 'DORMANT_ACCOUNT',
    label: 'عميل خامل',
    description:
      'عميل قائم لم يقع عليه نشاطٌ منذ أكثر من الحد. العميل الحاليّ أرخص في البيع من عميلٍ جديد، وخموله أوّلُ علامات انصرافه.',
    subject: 'ACCOUNT',
    defaultPriority: 'NORMAL',
    defaultEnabled: true,
    defaultThreshold: 60,
    thresholdUnit: 'DAYS',
    thresholdLabel: 'ينقطع النشاط أكثر من',
    action: 'CALL',
    campaignSubject: 'مكالمة استعادة عميل',
  },
] as const;

export const WAR_ROOM_RULE_MAP = Object.fromEntries(
  WAR_ROOM_RULES.map((r) => [r.key, r]),
) as Readonly<Record<WarRoomRuleKey, WarRoomRuleDef>>;

/* ==========================================================================
   شكل الإشارة
   ========================================================================== */

export interface WarRoomSignal {
  /** معرّف ثابت للإشارة: القاعدة والسجل. عليه يُبنى الصرف والتأجيل. */
  key: string;
  rule: WarRoomRuleKey;
  subject: SignalSubject;
  subjectId: string;
  priority: WarRoomPriority;
  /** عنوان السجل كما يُعرض. */
  title: string;
  /** سبب ظهور الإشارة بصيغة مقروءة. */
  reason: string;
  /** القيمة المالية المرتبطة، إن وُجدت. */
  amount?: number;
  /** شدة التجاوز بالأيام؛ تُستخدم في الترتيب داخل القاعدة. */
  overdueDays?: number;
  /**
   * رُفعت أولويّتُها لطولِ تجاوزها.
   *
   * تُعلَّم لا تُخفى: «حرجة» لأنّها تأخّرت ضعفَ الحدِّ تختلف عن «حرجة» بضبطِ
   * القاعدة — ومن يرى السببَ يثقُ بالترتيب.
   */
  escalated?: boolean;
  ownerId?: string | null;
  ownerName?: string | null;
  /** جهة الاتصال المرتبطة، لتكوين حملة اتصال. */
  phone?: string | null;
  accountId?: string | null;
  leadId?: string | null;
  dealId?: string | null;
}

/** المفتاح الثابت للإشارة. */
export function signalKey(rule: WarRoomRuleKey, subjectId: string): string {
  return `${rule}:${subjectId}`;
}

/** الأولويةُ درجةً أعلى — «عادي» يصيرُ «مرتفعًا» و«مرتفع» يصيرُ «حرجًا». */
export function raisePriority(p: WarRoomPriority): WarRoomPriority {
  return p === 'NORMAL' ? 'HIGH' : 'CRITICAL';
}

/**
 * مضاعِفُ التصعيد.
 *
 * ما تجاوز حدَّه بمقدارِ ضعفِه لم يعُد الحالةَ التي وُضع لها الحدّ: فاتورةٌ
 * تأخّرت ثلاثةَ أيّامٍ شيء، وتأخّرت تسعةً شيءٌ آخر. فتُرفع درجةً وتُعلَّم.
 */
export const ESCALATION_FACTOR = 3;

/**
 * تصعيدُ الإشارةِ الطاعنةِ في التأخّر.
 *
 * يخصُّ قواعدَ الأيّامِ وحدَها: حدُّ القيمةِ (صفقةٌ لا تقلُّ عن كذا) لا يُتجاوَز
 * بمرورِ الوقتِ فلا معنى لتصعيده به. ولا تُرفع إشارةٌ حرجةٌ أصلًا.
 */
export function escalateSignal(
  signal: WarRoomSignal,
  threshold: number,
  unit: WarRoomRuleDef['thresholdUnit'],
): WarRoomSignal {
  if (unit !== 'DAYS' || threshold <= 0) return signal;
  if (signal.priority === 'CRITICAL') return signal;
  if ((signal.overdueDays ?? 0) < threshold * ESCALATION_FACTOR) return signal;
  return { ...signal, priority: raisePriority(signal.priority), escalated: true };
}

/**
 * ترتيب الإشارات.
 *
 * الأولوية أولًا ثم شدة التجاوز ثم القيمة. الترتيب بالقيمة وحدها يدفع
 * الفواتير الصغيرة المتأخرة شهورًا إلى آخر القائمة فلا تُحصَّل أبدًا.
 */
export function compareSignals(a: WarRoomSignal, b: WarRoomSignal): number {
  const byPriority = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
  if (byPriority !== 0) return byPriority;

  const byOverdue = (b.overdueDays ?? 0) - (a.overdueDays ?? 0);
  if (byOverdue !== 0) return byOverdue;

  return (b.amount ?? 0) - (a.amount ?? 0);
}

/* ==========================================================================
   جلسات التركيز
   ========================================================================== */

/**
 * حالة الجلسة.
 *
 * `SCHEDULED` مُعلَنة ولم تبدأ، و`ACTIVE` جارية والمدعوّون أُشعروا،
 * و`ENDED` انتهت بوقتها أو بإنهاء المدير، و`CANCELLED` أُلغيت قبل بدئها.
 *
 * والفرق بين `ENDED` و`CANCELLED` ليس لفظيًا: المنتهية تُقاس — كم كان
 * المطلوب وكم أُنجز — والملغاة لا شيء فيها يُقاس، فخلطهما يُفسد أي متوسط.
 */
export const WAR_ROOM_SESSION_STATUSES = ['SCHEDULED', 'ACTIVE', 'ENDED', 'CANCELLED'] as const;
export type WarRoomSessionStatus = (typeof WAR_ROOM_SESSION_STATUSES)[number];

export interface WarRoomSessionStatusDef {
  key: WarRoomSessionStatus;
  label: string;
  kind: StatusKind;
}

export const WAR_ROOM_SESSION_STATUS_DEFS: readonly WarRoomSessionStatusDef[] = [
  { key: 'SCHEDULED', label: 'مجدولة', kind: 'info' },
  { key: 'ACTIVE', label: 'جارية', kind: 'warning' },
  { key: 'ENDED', label: 'انتهت', kind: 'success' },
  { key: 'CANCELLED', label: 'أُلغيت', kind: 'neutral' },
] as const;

export const WAR_ROOM_SESSION_STATUS_MAP = Object.fromEntries(
  WAR_ROOM_SESSION_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<WarRoomSessionStatus, WarRoomSessionStatusDef>>;

/** أطول جلسة مقبولة بالدقائق. */
export const MAX_SESSION_MINUTES = 8 * 60;

/** أقصر جلسة مقبولة بالدقائق. */
export const MIN_SESSION_MINUTES = 10;

export interface WarRoomSessionView {
  id: string;
  title: string;
  /** نطاقُ الجلسة — فارغُه كلُّ القواعد المفعَّلة. */
  rules: WarRoomRuleKey[];
  status: WarRoomSessionStatus;
  startsAt: string;
  endsAt: string;
  targetCount: number;
  /** ما تبقّى من إشارات في نطاق الجلسة الآن. */
  remaining: number;
  /** ما أُنجز: الفرق بين اللقطة والمتبقّي، ولا ينزل تحت الصفر. */
  done: number;
  /**
   * إشارات ظهرت بعد البدء فتجاوز المتبقّي اللقطة.
   *
   * تُعرض صراحةً ولا تُخفى بقصّ الرقم: جلسةٌ يزيد فيها العمل حقيقةٌ تخصّ من
   * يديرها، وإخفاؤها يجعل «صفر منجَز» يبدو تقصيرًا وهو ليس كذلك.
   */
  appeared: number;
  participants: Array<{ userId: string; joinedAt: string | null }>;
  startedAt: string | null;
  endedAt: string | null;
}

/**
 * نسبة الإنجاز.
 *
 * دالة خالصة يستعملها الخادم والواجهة معًا. والقسمة على صفر ليست حالة خطأ
 * بل حالة معروفة: جلسة بدأت ولا إشارات في نطاقها منجَزةٌ بالكامل — لا
 * ناقصة.
 */
export function sessionProgress(session: {
  targetCount: number;
  done: number;
}): number {
  if (session.targetCount <= 0) return 100;
  return Math.min(100, Math.round((session.done / session.targetCount) * 100));
}

/**
 * الدقائق المتبقية، سالبةً إن تجاوزت الجلسة وقتها.
 *
 * السالب مقصود: «تجاوزت بعشر دقائق» معلومة، و«صفر» تُخفيها.
 */
export function minutesLeft(endsAt: string | Date, now: Date = new Date()): number {
  const end = typeof endsAt === 'string' ? new Date(endsAt) : endsAt;
  return Math.round((end.getTime() - now.getTime()) / 60_000);
}
